import { ReactNode, ReactElement } from "react";
import HeaderBase, {
  AligmentTypes,
} from "@/components/_headers/HeaderBase/HeaderBase";
import { columnSize } from "@/utils/layout/columnSize";
import { LinkType, ImageType, ColumnsType } from "@/types/baseTypes";
import { Section } from "@/components/Section/Section";
import { CutOutImage } from "@/components/CutOutImage/CutOutImage";
import { CustomLink } from "@/components/CustomLink";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import styles from "./UniversalHeader.module.scss";
import { downloadFile } from "@/utils/downloadFile.ts";

export interface UniversalHeaderProps {
  type: "textheader" | "imageheader" | "splitheader";
  mediaJSX?: ReactNode;
  contentJSX: ReactNode;
  breadcrumbsJSX: ReactNode;
  background_color_class?: string | false;
  section_margin_class?: string;

  // Text Header specific
  textheaderTextWidth?: ColumnsType[] | false;
  text_horizontal_align?: AligmentTypes;

  // Split/Image Header specific
  image_small?: ImageType | false;
  link?: LinkType;
  linkJSX?: ReactNode;
}

export const UniversalHeader = ({
  type,
  mediaJSX,
  contentJSX,
  breadcrumbsJSX,
  background_color_class,
  section_margin_class,
  textheaderTextWidth,
  text_horizontal_align,
  image_small,
  link,
  linkJSX,
}: UniversalHeaderProps) => {
  const { getIcon } = useGlobalContext();

  const handleBooleanClass = (cls: string | false | undefined) => cls || "";

  switch (type) {
    case "textheader":
      return (
        <HeaderBase
          className={`${styles["text-header"]} ${handleBooleanClass(background_color_class)} ${section_margin_class || ""} ${styles[`align-text-${text_horizontal_align}`]}`}
          header={breadcrumbsJSX as ReactElement}
          content={contentJSX as ReactElement}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(textheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment={"center"}
          height={"content-height"}
        />
      );
    case "splitheader":
    case "imageheader":
      return (
        <Section
          disableContainer
          className={styles["page-header"]}
          tag="header"
          disableClip
          disableColumns
          data-header-type={type}
        >
          <div className={`h-100 ${styles["content-wrapper"]}`}>
            <div className={styles["breadcrumbs"]}>{breadcrumbsJSX}</div>
            <div className={`${styles["background-image"]}`}>{mediaJSX}</div>
            <div className={`${styles["content"]} mt-4`}>{contentJSX}</div>
            {linkJSX && <div className={styles["link-area"]}>{linkJSX}</div>}
          </div>
          {type === "splitheader" && (
            <div className="is-relative">
              {image_small && (
                <CutOutImage
                  imageSrc={image_small.url}
                  imageAlt={image_small.alt}
                  notchX={72}
                  notchY={72}
                  containerClassName={styles["cutout-container"]}
                />
              )}

              {link && (
                <>
                  {link.type == "download" ? (
                    <button
                      aria-label={link.aria_label}
                      className={styles["overlay-link"]}
                      onClick={(e) => {
                        if (link.type == "download") {
                          e.preventDefault();
                          e.stopPropagation();
                          downloadFile(link.url).catch((e) => console.error(e));
                        }
                      }}
                    >
                      {""}
                    </button>
                  ) : (
                    <CustomLink
                      href={link.url}
                      target={link.target}
                      aria-label={link.aria_label}
                      className={styles["overlay-link"]}
                      type={link.type}
                      onClick={(e) => {
                        if (link.type == "download") {
                          e.preventDefault();
                          e.stopPropagation();
                          downloadFile(link.url).catch((e) => console.error(e));
                        }
                      }}
                    >
                      {""}
                    </CustomLink>
                  )}
                  <span className={styles["link-btn"]}>
                    <CustomSVG
                      src={getIcon("ArrowBigRight")}
                      width={14}
                      height={14}
                      wrapper="span"
                      className="is-flex"
                    />
                  </span>
                </>
              )}
            </div>
          )}
        </Section>
      );
    default:
      // Fallback or error handling if type is unknown, though types enforce this shouldn't happen
      return <></>;
  }
};
